import { useEffect, useRef, useState } from "react";

import { pingHealth } from "@/services/health/ApiHealthService";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";

export type ApiHealthGateStatus = "checking" | "ok" | "degraded" | "down";

export interface UseApiHealthGateOptions {
  scheduleMs?: number[];
}

export interface UseApiHealthGateResult {
  isRetrying: boolean;
  status: ApiHealthGateStatus;
  retry: (source?: "manual" | "automatic") => void;
}

const DEFAULT_RETRY_SCHEDULE_MS: number[] = [
  0, 1000, 5000, 10000, 30000, 45000, 60000,
];
const MAX_CHECKING_DURATION_MS = 10_000;
const logger =
  FrontendLoggerFactory.getLogger().withComponent("useApiHealthGate");

export const useApiHealthGate = (
  options?: UseApiHealthGateOptions,
): UseApiHealthGateResult => {
  const [status, setStatus] = useState<ApiHealthGateStatus>("checking");
  const [retryToken, setRetryToken] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  const timeoutIdRef = useRef<number | null>(null);
  const scheduleRef = useRef<number[] | null>(null);
  const attemptGenerationRef = useRef(0);
  const manualRetryGenerationRef = useRef<number | null>(null);

  if (scheduleRef.current === null) {
    const schedule =
      options?.scheduleMs && options.scheduleMs.length > 0
        ? [...options.scheduleMs].sort((a, b) => a - b)
        : DEFAULT_RETRY_SCHEDULE_MS;
    scheduleRef.current = schedule;
  }

  useEffect(() => {
    if (status !== "checking") return undefined;

    // CRITICAL PD-51: a paused backend or a stalled response body must not
    // leave the cold-load gate checking indefinitely, even across retries.
    const timeoutId = window.setTimeout(() => {
      setStatus((current) => (current === "checking" ? "down" : current));
    }, MAX_CHECKING_DURATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [status]);

  useEffect(() => {
    const lifecycle = { cancelled: false };
    const generation = attemptGenerationRef.current;
    const isCancelled = () =>
      lifecycle.cancelled || attemptGenerationRef.current !== generation;
    const abortController = new AbortController();
    const schedule = scheduleRef.current;
    if (schedule === null) {
      return undefined;
    }

    const clearTimer = () => {
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };

    const runAttempt = async (index: number) => {
      if (isCancelled()) {
        return;
      }

      logger.debug("Running admin API health attempt", {
        operation: "api_health_attempt_start",
        attemptIndex: index,
        retryToken,
        scheduleMs: schedule,
      });
      const result = await pingHealth(abortController.signal);
      if (isCancelled()) {
        return;
      }
      if (manualRetryGenerationRef.current === generation) {
        manualRetryGenerationRef.current = null;
        setIsRetrying(false);
      }
      if (result === "ok" || result === "degraded") {
        logger.info("Admin API health gate recovered", {
          operation: "api_health_attempt_success",
          attemptIndex: index,
          retryToken,
          result,
        });
        setStatus(result);
        clearTimer();
        return;
      }

      logger.debug("Admin API health attempt reported unhealthy", {
        operation: "api_health_attempt_unhealthy",
        attemptIndex: index,
        retryToken,
        result,
      });
      // CRITICAL PD-332 r1: a first failed or timed-out probe must reveal the
      // recoverable outage without waiting for a second probe.
      setStatus("down");

      const nextIndex = index + 1;
      if (nextIndex >= schedule.length) {
        logger.warn("Admin API health retries exhausted", {
          operation: "api_health_retry_exhausted",
          attemptIndex: index,
          retryToken,
          scheduleMs: schedule,
        });
        return;
      }

      const delay = schedule[nextIndex] - schedule[index];
      logger.debug("Scheduling next admin API health retry", {
        operation: "api_health_retry_scheduled",
        attemptIndex: index,
        nextAttemptIndex: nextIndex,
        retryToken,
        nextDelayMs: Math.max(delay, 0),
        scheduleMs: schedule,
      });
      timeoutIdRef.current = window.setTimeout(
        () => {
          void runAttempt(nextIndex);
        },
        Math.max(delay, 0),
      );
    };

    void runAttempt(0);

    return () => {
      lifecycle.cancelled = true;
      abortController.abort();
      clearTimer();
    };
  }, [retryToken]);

  return {
    isRetrying,
    status,
    retry: (source = "manual") => {
      // A transport event must not cancel an explicit check or keep its
      // feedback busy indefinitely. The generation also excludes stale results.
      if (manualRetryGenerationRef.current !== null) return;
      const generation = attemptGenerationRef.current + 1;
      attemptGenerationRef.current = generation;
      if (source === "manual") {
        manualRetryGenerationRef.current = generation;
        setIsRetrying(true);
      }
      logger.info("Admin API health retry requested", {
        operation: "api_health_retry_manual",
        retryToken,
        source,
      });
      setStatus((current) => (current === "down" ? current : "checking"));
      setRetryToken((current) => current + 1);
    },
  };
};
